import { sanitizeFileName } from '../../services/calendarService';
import { heuteBerlin } from '../../services/dateService';
import {
    KICKOFF_MAX_SPAETER,
    type KickoffAck,
    type KickoffBereich,
    type KickoffHinweisArt,
    type KickoffPlan,
    type KickoffTerminArt,
} from '../../services/kickoff/kickoffTypes';

/**
 * Entscheidungslogik des Kickoff-Popups (docs/projekt-kickoff-plan.md §4.3,
 * §4.2a). Rein und ohne React, damit sie ohne DOM testbar ist — der Dialog
 * selbst zeigt nur an, was hier entschieden wird.
 */

/**
 * Fensterereignis „Kickoff-Stand hat sich geändert“: wer es auslöst (nach
 * einer Projektanlage, nach einer Bestätigung), lässt das Gate neu laden.
 */
export const KICKOFF_AKTUALISIEREN = 'kickoff:aktualisieren';

/**
 * Eine offene Quittung samt Terminplan. `plan` ist null und `fehler` gesetzt,
 * wenn das Projekt nicht ladbar war (Netz, gelöscht, keine Rechte) oder der
 * Plan nicht gebaut werden konnte.
 */
export interface KickoffDialogEintrag {
    ack: KickoffAck;
    plan: KickoffPlan | null;
    fehler?: string;
}

/**
 * Warum dieses Projekt gerade nicht bestätigt werden kann, oder null.
 *
 * Ohne Plan weiß niemand, welche Termine in den Kalender gehören — ein
 * „Zur Kenntnis genommen“ quittierte dann ein Projekt, das der Nutzer nie
 * gesehen hat, und es käme nie wieder. Deshalb: kein Bestätigen, die Zeile
 * bleibt offen und erscheint beim nächsten Start wieder. Ein Plan von null
 * ohne Fehlertext (älterer Datendienst) zählt genauso.
 */
export const eintragFehler = (eintrag: Pick<KickoffDialogEintrag, 'plan' | 'fehler'>): string | null => {
    const text = (eintrag.fehler ?? '').trim();
    if (text) return text;
    return eintrag.plan === null ? 'Projektdaten nicht ladbar' : null;
};

// ── Sperre und „Später“ ─────────────────────────────────────────────────────

/** Hat dieses Projekt sein Kontingent an „Später“ verbraucht? */
export const istAusgeschoepft = (ack: Pick<KickoffAck, 'spaeter_anzahl'>): boolean =>
    (ack.spaeter_anzahl ?? 0) >= KICKOFF_MAX_SPAETER;

/**
 * Sperrt das Popup? Ja, sobald EIN gezeigtes Projekt dreimal verschoben wurde.
 *
 * Bewusst „eines genügt“: „Später“ zählt für alle gezeigten Projekte
 * gemeinsam (§4.2a). Bliebe der Knopf stehen, solange noch ein jüngeres
 * Projekt verschiebbar ist, käme das ausgeschöpfte Projekt nie zur
 * Bestätigung. Ist es bestätigt, fällt es aus der Liste — und für die
 * jüngeren erscheint „Später“ von selbst wieder.
 */
export const istGesperrt = (acks: ReadonlyArray<Pick<KickoffAck, 'spaeter_anzahl'>>): boolean =>
    acks.some(istAusgeschoepft);

/**
 * n in „Später (noch n×)“: 3 minus der höchste Zählerstand der gezeigten
 * Projekte. Der höchste, nicht der niedrigste — der nächste Klick erhöht alle
 * gezeigten Zähler, und das am weitesten verschobene Projekt sperrt zuerst.
 * Leere Liste: volles Kontingent (es gibt dann ohnehin nichts zu zeigen).
 */
export const spaeterRest = (acks: ReadonlyArray<Pick<KickoffAck, 'spaeter_anzahl'>>): number => {
    const hoechster = acks.reduce((max, a) => Math.max(max, a.spaeter_anzahl ?? 0), 0);
    return Math.max(0, KICKOFF_MAX_SPAETER - hoechster);
};

/**
 * Welche Einträge zeigt das Popup?
 *
 * `ausgeblendet` sind die Zeilen, die in dieser Sitzung schon weg sind: per
 * „Später“ verschoben oder bei einer Störung ohne Zählen geschlossen. Beide
 * bleiben bis zum nächsten App-Start weg. Kommt zwischendurch ein neues
 * Projekt dazu (Ereignis `kickoff:aktualisieren`), erscheint es trotzdem —
 * aber ohne die schon ausgeblendeten: die würden sonst in derselben Sitzung
 * ein zweites Mal gezählt, obwohl der Nutzer gerade erst „Später“ gesagt hat.
 */
export const sichtbareEintraege = <T extends { ack: Pick<KickoffAck, 'id' | 'bestaetigt_am'> }>(
    eintraege: ReadonlyArray<T>,
    ausgeblendet: ReadonlySet<string>,
): T[] => eintraege.filter(e => !e.ack.bestaetigt_am && !ausgeblendet.has(e.ack.id));

/**
 * Antwort von `kickoff_spaeter` je Zeile. `erhoeht` sagt, ob DIESER Aufruf den
 * Zähler erhöht hat; fehlt es (ältere RPC), gilt es als nicht erhöht.
 */
export interface SpaeterStand {
    id: string;
    spaeter_anzahl: number;
    erhoeht?: boolean;
}

export interface SpaeterErgebnis<T> {
    /** Die Einträge mit übernommenem Zählerstand, ohne die nicht mehr offenen. */
    eintraege: T[];
    /** Wirklich verschoben — bis zum nächsten Start ausblenden. */
    ausblenden: string[];
    /** Gefragt, aber nicht erhöht (zweites Gerät war schneller) — bleiben sichtbar. */
    nichtVerschoben: string[];
}

/**
 * Wertet die Antwort von „Später“ aus.
 *
 * Nur Zeilen mit `erhoeht: true` verschwinden. Hat ein zweites Gerät den
 * Zähler inzwischen auf 3 gebracht, kommt die Zeile unerhöht mit Stand 3
 * zurück — sie bleibt stehen, und die Sperre greift sofort, statt bis zum
 * nächsten Start zu warten. Eine gefragte Zeile, die gar nicht zurückkommt,
 * ist nicht mehr offen (anderswo bestätigt oder Projekt gelöscht) und fällt
 * aus der Liste. Nicht gefragte Zeilen bleiben unberührt.
 */
export const spaeterAuswerten = <T extends { ack: KickoffAck }>(
    eintraege: ReadonlyArray<T>,
    gefragt: ReadonlyArray<string>,
    staende: ReadonlyArray<SpaeterStand>,
): SpaeterErgebnis<T> => {
    const gefragtSet = new Set(gefragt);
    const antwort = new Map(staende.map(s => [s.id, s]));
    const ausblenden: string[] = [];
    const nichtVerschoben: string[] = [];
    const bleiben: T[] = [];
    for (const e of eintraege) {
        if (!gefragtSet.has(e.ack.id)) { bleiben.push(e); continue; }
        const stand = antwort.get(e.ack.id);
        if (!stand) continue;
        if (stand.erhoeht === true) ausblenden.push(e.ack.id);
        else nichtVerschoben.push(e.ack.id);
        bleiben.push({ ...e, ack: { ...e.ack, spaeter_anzahl: stand.spaeter_anzahl } });
    }
    return { eintraege: bleiben, ausblenden, nichtVerschoben };
};

/** Die Ids, die „Später“ wirklich verschoben hat — nur gefragte, nur `erhoeht: true`. */
export const verschobeneIds = (gefragt: ReadonlyArray<string>, staende: ReadonlyArray<SpaeterStand>): string[] => {
    const gefragtSet = new Set(gefragt);
    return staende.filter(s => s.erhoeht === true && gefragtSet.has(s.id)).map(s => s.id);
};

/** Satz für den Dialog, wenn „Später“ nicht für alle gezeigten Projekte gegriffen hat. */
export const nichtVerschobenMeldung = (anzahl: number): string =>
    anzahl === 1
        ? 'Ein Projekt wurde schon auf einem anderen Gerät dreimal verschoben – bitte jetzt bestätigen.'
        : `${anzahl} Projekte wurden schon auf einem anderen Gerät dreimal verschoben – bitte jetzt bestätigen.`;

// ── Störung: die Sperre darf nie an der Technik hängen ──────────────────────

/**
 * Was der Dialog gerade anbietet.
 *
 * - `verschiebbar`: ×, Esc und „Später (noch n×)“ zählen auf dem Server.
 * - `gesperrt`: kein Schließen-Weg, nur Bestätigen.
 * - `stoerung`: Bestätigen, Später oder Laden ist technisch gescheitert
 *   (Netz weg, Server-Fehler). Die RPCs antworten fachlich nie mit einem
 *   Fehler (sie liefern Zeilen bzw. null) — jeder geworfene Fehler ist also
 *   technisch. Dann darf die Sperre die App nicht festhalten: Schließen ist
 *   möglich, OHNE etwas zu verbuchen, und beim nächsten Start kommt das Popup
 *   wieder. Die Störung schlägt die Sperre, weil eine Sperre ohne
 *   funktionierenden Ausweg die App unbenutzbar machte.
 */
export type DialogModus =
    | { art: 'verschiebbar'; rest: number }
    | { art: 'gesperrt' }
    | { art: 'stoerung'; hinweis: string };

/** Hinweis im Störungsfall; `grund` ist die Fehlermeldung des Servers bzw. Netzes. */
export const stoerungsHinweis = (grund: string): string => {
    const g = grund.trim();
    return `Bestätigung gerade nicht möglich${g ? ` (${g})` : ''} – das Popup erscheint beim nächsten Start wieder.`;
};

export const dialogModus = (
    eintraege: ReadonlyArray<Pick<KickoffDialogEintrag, 'ack' | 'plan' | 'fehler'>>,
    stoerung: string | null,
): DialogModus => {
    // Ein nicht ladbares Projekt ist eine Störung wie ein Netzfehler: es lässt
    // sich nicht bestätigen, also darf es auch nicht sperren.
    const grund = stoerung ?? eintraege.map(eintragFehler).find((f): f is string => f !== null) ?? null;
    if (grund !== null) return { art: 'stoerung', hinweis: stoerungsHinweis(grund) };
    const acks = eintraege.map(e => e.ack);
    if (istGesperrt(acks)) return { art: 'gesperrt' };
    return { art: 'verschiebbar', rest: spaeterRest(acks) };
};

// ── Fokusfalle ──────────────────────────────────────────────────────────────

/**
 * Wohin springt Tab bzw. Umschalt+Tab? Index des Ziels unter den
 * bedienbaren Elementen des Dialogs, oder null = Browser machen lassen.
 * `aktuell` = -1, wenn der Fokus gerade nicht im Dialog liegt (dann an den
 * Anfang bzw. ans Ende). Zyklisch: vom letzten zum ersten und umgekehrt.
 */
export const fokusZiel = (anzahl: number, aktuell: number, rueckwaerts: boolean): number | null => {
    if (anzahl <= 0) return null;
    if (aktuell < 0) return rueckwaerts ? anzahl - 1 : 0;
    if (!rueckwaerts && aktuell >= anzahl - 1) return 0;
    if (rueckwaerts && aktuell <= 0) return anzahl - 1;
    return null;
};

// ── Haken je Termin ─────────────────────────────────────────────────────────

/** Schlüssel des Hakens „Zur Kenntnis genommen“ bei Projekten ohne Termine. */
export const KENNTNIS_HAKEN = 'kenntnis';

/**
 * Schlüssel eines Hakens. Über die Quittungs-id, nicht die Projekt-id: dieselbe
 * Person kann für ein Projekt zwei offene Zeilen haben (Planung und
 * Ausführung getrennt angelegt), und deren Haken dürfen sich nicht teilen.
 */
export const hakenSchluessel = (ackId: string, terminUid: string): string => `${ackId}|${terminUid}`;

/**
 * Schlüssel des Hakens „SiGe-Plan-Termin selbst eingetragen“. Steht der Plan
 * ohne Samstagsplatz da (Hinweis `sigeplan_ohne_platz`), fehlt die Serie in
 * der Kalenderdatei. Ohne eigenen Haken bestätigte der Nutzer das Projekt
 * endgültig, und die Serie würde nie nachgeholt — also muss er ausdrücklich
 * bestätigen, dass er sie selbst eingetragen hat.
 */
export const SIGEPLAN_SELBST_HAKEN = 'sigeplan-selbst';

/** Fehlt die Samstagsserie im Plan und muss von Hand eingetragen werden? */
export const sigeplanSelbstEintragen = (plan: KickoffPlan | null): boolean =>
    !!plan && plan.hinweise.some(h => h.art === 'sigeplan_ohne_platz');

/**
 * Alle Haken, die ein Projekt zum Bestätigen braucht: einer je Termin, dazu
 * „SiGe-Plan selbst eingetragen“, wenn die Serie keinen Platz bekam. Bleibt
 * nichts übrig, genau einer „Zur Kenntnis genommen“ — auch ein Projekt ohne
 * Baustart muss jemand bewusst zur Kenntnis nehmen. Ein nicht ladbares
 * Projekt hat keine Haken: es lässt sich nicht bestätigen (`eintragFehler`).
 */
export const pflichtHaken = (eintrag: KickoffDialogEintrag): string[] => {
    if (eintragFehler(eintrag) !== null) return [];
    const id = eintrag.ack.id;
    const haken = (eintrag.plan?.termine ?? []).map(t => hakenSchluessel(id, t.uid));
    if (sigeplanSelbstEintragen(eintrag.plan)) haken.push(hakenSchluessel(id, SIGEPLAN_SELBST_HAKEN));
    return haken.length > 0 ? haken : [hakenSchluessel(id, KENNTNIS_HAKEN)];
};

/** „Bestätigen“ erst, wenn jeder Haken des Projekts gesetzt ist — nie bei einem nicht ladbaren Projekt. */
export const alleHakenGesetzt = (eintrag: KickoffDialogEintrag, gesetzt: ReadonlySet<string>): boolean =>
    eintragFehler(eintrag) === null && pflichtHaken(eintrag).every(k => gesetzt.has(k));

/** Schaltet einen Haken um — liefert ein neues Set, damit React den Wechsel sieht. */
export const hakenUmschalten = (gesetzt: ReadonlySet<string>, schluessel: string): Set<string> => {
    const neu = new Set(gesetzt);
    if (neu.has(schluessel)) neu.delete(schluessel);
    else neu.add(schluessel);
    return neu;
};

// ── Kalenderdatei ───────────────────────────────────────────────────────────

/** Pläne, die überhaupt etwas in den Kalender bringen. */
export const plaeneMitTerminen = (eintraege: ReadonlyArray<KickoffDialogEintrag>): KickoffPlan[] =>
    eintraege
        .map(e => e.plan)
        .filter((p): p is KickoffPlan => !!p && p.termine.length > 0);

/** Zeilenumbrüche und Mehrfach-Leerzeichen aus Nutzereingaben raus — wie im Mailanhang. */
const einzeilig = (wert: string | undefined | null): string => String(wert ?? '').replace(/\s+/g, ' ').trim();

/**
 * Dateiname der Kalenderdatei, gleich wie im Mailanhang
 * (`buildKickoffIcsFilename` in kickoffMailer.ts): ein Projekt
 * `Projekt_<nr>_<name>.ics`, mehrere `Neue_Projekte_<datum>.ics` mit dem
 * Berliner Kalendertag. Gleiche Namen in Mail und Popup, damit niemand zwei
 * Dateien für dieselben Termine vermutet. Eigene Fassung statt Import, weil
 * der Mailer den Supabase-Client mitzieht; ein Test hält beide gleich.
 */
export const icsDateiname = (plaene: ReadonlyArray<Pick<KickoffPlan, 'projectNumber' | 'projectName'>>, jetzt: Date): string => {
    if (plaene.length === 1) {
        const titel = [einzeilig(plaene[0].projectNumber), einzeilig(plaene[0].projectName)].filter(Boolean).join('_');
        return titel ? `Projekt_${sanitizeFileName(titel)}.ics` : 'Projekt.ics';
    }
    return `Neue_Projekte_${sanitizeFileName(heuteBerlin(jetzt))}.ics`;
};

/** Projekt-ids für „Mail erneut senden“ — jedes Projekt nur einmal. */
export const projektIds = (eintraege: ReadonlyArray<KickoffDialogEintrag>): string[] =>
    [...new Set(eintraege.map(e => e.ack.project_id))];

// ── Anzeige ─────────────────────────────────────────────────────────────────

const TERMIN_ART_NAME: Record<KickoffTerminArt, string> = {
    vorank: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    begehung: 'Begehungen',
    einweisung: 'Einweisung',
};

/** Kurzname des Termins vor der Anzeige („Begehungen · jeden Di + Do 08:00“). */
export const terminArtName = (art: KickoffTerminArt): string => TERMIN_ART_NAME[art] ?? art;

/** Kennzeichen am Projektblock; bei 'alle' keins — das ist der Normalfall. */
export const bereichKennzeichen = (bereich: KickoffBereich): string | null =>
    bereich === 'planung' ? 'Planung' : bereich === 'ausfuehrung' ? 'Ausführung' : null;

/**
 * Rot = es fehlt etwas oder eine Frist ist schon vorbei; Orange = beachten,
 * aber nichts verpasst (manueller Turnus, Überschneidung mit eigenem Termin,
 * SiGe-Plan ohne Samstagsplatz). Neue Hinweisarten landen damit von selbst in
 * Orange — Rot bleibt den beiden Fällen vorbehalten, in denen schon etwas fehlt.
 */
export const hinweisStufe = (art: KickoffHinweisArt): 'rot' | 'orange' =>
    art === 'baustart_fehlt' || art === 'frist_ueberschritten' ? 'rot' : 'orange';

/** Überschrift des Popups; im Sperrfall mit Begründung, damit niemand einen Fehler vermutet. */
export const dialogTitel = (anzahl: number, gesperrt: boolean): string => {
    if (gesperrt) return 'Bitte bestätigen – dreimal verschoben';
    return anzahl === 1 ? 'Neues Projekt zugewiesen' : `${anzahl} neue Projekte zugewiesen`;
};

// ── Rückmeldung „Mail erneut senden“ ────────────────────────────────────────

/** Was `benachrichtigeKickoff` je Empfänger meldet (Ausschnitt von KickoffVersandErgebnis). */
export interface VersandRueckmeldung {
    email: string;
    ok: boolean;
    error?: string;
}

export interface DialogMeldung {
    art: 'ok' | 'fehler';
    text: string;
}

/**
 * Ein Satz für die Statuszeile. Eine leere Ergebnisliste ist KEIN Erfolg:
 * dann wurde nichts versucht (Migration fehlt oder keine passende Zeile), und
 * „gesendet“ wäre gelogen.
 */
export const versandMeldung = (ergebnisse: ReadonlyArray<VersandRueckmeldung>): DialogMeldung => {
    if (ergebnisse.length === 0) {
        return { art: 'fehler', text: 'Es wurde keine Mail verschickt – keine offene Zuweisung gefunden.' };
    }
    const fehler = ergebnisse.find(e => !e.ok);
    if (fehler) {
        return { art: 'fehler', text: `Mail nicht gesendet: ${fehler.error || 'unbekannter Fehler'}` };
    }
    const adressen = [...new Set(ergebnisse.map(e => e.email).filter(Boolean))];
    return { art: 'ok', text: adressen.length ? `Mail an ${adressen.join(', ')} gesendet.` : 'Mail gesendet.' };
};
